import React from 'react';
import {observer} from 'mobx-react';
import {observable, action} from 'mobx';
import classNames from 'classnames';
import {TechseeBaseComponent} from '@techsee/techsee-ui-common/lib/_shared/techsee-base-component';
import {SimpleLabel} from '@techsee/techsee-ui-common/lib/forms/labels/simple';

import './session-tags-select.scss';

export interface ISessionTagsSelectProps {
    label: string;
    placeholder: string;
    options: string[];
    selected: ReadonlyArray<string>;
    max: number;
    onToggle(tag: string): void;
    onRemove(tag: string): void;
}

@observer
export class SessionTagsSelect extends TechseeBaseComponent<ISessionTagsSelectProps> {
    @observable private _isOpen: boolean = false;

    private _ref = React.createRef<HTMLDivElement>();

    constructor(props: ISessionTagsSelectProps) {
        super(props);

        this._handleClickOutside = this._handleClickOutside.bind(this);
    }

    componentWillUnmount(): void {
        document.removeEventListener('mousedown', this._handleClickOutside);
    }

    @action
    private _setOpen(open: boolean): void {
        this._isOpen = open;

        if (open) {
            document.removeEventListener('mousedown', this._handleClickOutside);
            document.addEventListener('mousedown', this._handleClickOutside);
        } else {
            document.removeEventListener('mousedown', this._handleClickOutside);
        }
    }

    private _handleClickOutside(event: MouseEvent): void {
        if (this._ref.current && !this._ref.current.contains(event.target as Node)) {
            this._setOpen(false);
        }
    }

    private _toggleOpen = (): void => {
        this._setOpen(!this._isOpen);
    };

    private _onOptionClick = (event: React.MouseEvent, tag: string): void => {
        event.preventDefault();
        event.stopPropagation();

        const isSelected = this.props.selected.indexOf(tag) > -1;

        // Block adding new tags once the cap is reached; already-selected tags can still be toggled off.
        if (!isSelected && this.props.selected.length >= this.props.max) {
            return;
        }

        this.props.onToggle(tag);
    };

    private _onRemoveClick = (event: React.MouseEvent | React.KeyboardEvent, tag: string): void => {
        event.preventDefault();
        event.stopPropagation();

        this.props.onRemove(tag);
    };

    private _handleRemoveClick = (event: React.MouseEvent): void => {
        const tag = (event.currentTarget as HTMLElement).getAttribute('data-tag');
        if (tag) {
            this._onRemoveClick(event, tag);
        }
    };

    private _handleRemoveKeyDown = (event: React.KeyboardEvent): void => {
        if (event.key === 'Enter' || event.key === ' ') {
            const tag = (event.currentTarget as HTMLElement).getAttribute('data-tag');
            if (tag) {
                this._onRemoveClick(event, tag);
            }
        }
    };

    private _handleOptionClick = (event: React.MouseEvent): void => {
        const tag = (event.currentTarget as HTMLElement).getAttribute('data-tag');
        if (tag) {
            this._onOptionClick(event, tag);
        }
    };

    renderInternal(): JSX.Element {
        const {label, placeholder, options, selected, max} = this.props;
        const hasSelection = selected.length > 0;
        const maxReached = selected.length >= max;
        const selectedSet = new Set(selected);

        return (
            <div className='session-tags-select' ref={this._ref}>
                <SimpleLabel>{label}</SimpleLabel>
                <div className={classNames('session-tags-control', {open: this._isOpen})} onClick={this._toggleOpen}>
                    <div className='session-tags-values'>
                        {!hasSelection && <span className='session-tags-placeholder'>{placeholder}</span>}
                        {selected.map((tag) => (
                            <span className='session-tag-chip' key={tag}>
                                <span className='session-tag-chip-text'>{tag}</span>
                                <i
                                    className='session-tag-remove'
                                    role='button'
                                    tabIndex={0}
                                    aria-label={`Remove ${tag}`}
                                    data-tag={tag}
                                    onClick={this._handleRemoveClick}
                                    onKeyDown={this._handleRemoveKeyDown}>
                                    ✕
                                </i>
                            </span>
                        ))}
                    </div>
                    <span className={classNames('select-arrow', {open: this._isOpen})} />
                </div>
                {this._isOpen && (
                    <ul className='session-tags-list'>
                        {options.map((tag) => {
                            const isSelected = selectedSet.has(tag);
                            const disabled = !isSelected && maxReached;

                            return (
                                <li
                                    key={tag}
                                    className={classNames('session-tags-item', {selected: isSelected, disabled})}
                                    data-tag={tag}
                                    onClick={this._handleOptionClick}>
                                    {tag}
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>
        );
    }
}

export default SessionTagsSelect;
